// frontend/src/pages/ListaDocumentacion.jsx
// LISTA DE DOCUMENTACIÓN USANDO COMPONENTE UNIFICADO
import React, { useState, useRef } from 'react';
import styled, { useTheme } from 'styled-components';
import ListaUnificada from '../components/common/ListaUnificada.jsx';
import { ModuloHeader, ModuloShell } from '../components/common/ModuloHeader.jsx';
import { LinkEquipo } from '../components/common/Links.jsx';
import ModalDocumentacion from '../components/formularios/ModalDocumentacion';
import ModalAdjuntosDocumentacion from '../components/formularios/ModalAdjuntosDocumentacion';
import ModalCargaMasivaDocumentacion from '../components/formularios/ModalCargaMasivaDocumentacion';
import { useAuth } from '../hooks/useAuth';

const LinkAdjuntos = styled.a`
  color: ${({ theme }) => theme.colors.info};
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const HELP = {
  prereqs: [
    'Tener definidos los tipos de documentación que se controlan por equipo.',
  ],
  tips: [
    'Cargá la fecha de vencimiento de cada documento: es lo que permite anticipar la renovación.',
    'Podés adjuntar el archivo escaneado para tenerlo disponible cuando lo pidan en obra.',
    'Un equipo con documentación vencida no debería salir a trabajar: revisá la lista antes de asignarlo.',
  ],
  nextSteps: [
    'Consultar el informe de documentación para ver los vencimientos próximos de toda la flota.',
    'Renovar el documento y actualizar su fecha una vez tramitado.',
  ],
};

// using shared LinkEquipo

const ListaDocumentacion = () => {
  const theme = useTheme();
  const { hasPermission } = useAuth();
  // Estados para formularios React
  const [showModal, setShowModal] = useState(false);
  const [editingDocumento, setEditingDocumento] = useState(null);
  const [documentoAdjuntos, setDocumentoAdjuntos] = useState(null);
  const [showCargaMasiva, setShowCargaMasiva] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const puedeCargarMasivo = hasPermission('documentacion', 'add');

  // Ref para acceder directamente a ListaUnificada y forzar refresco
  const listaUnificadaRef = useRef();

  // ============================= CONFIGURACIÓN DE COLUMNAS =============================
  // Configuración de columnas basada en el modelo Documentacion REAL
  const columns = [
    {
      key: 'equipo_interno',
      label: 'Equipo',
      sortable: true,
      sortKey: 'equipo__interno', // Campo real en el backend para ordenamiento
      width: '8%',
      render: (value, item) => (
    <LinkEquipo // using shared LinkEquipo
          href={`/?equipoId=${item.equipo}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.location.href = `/?equipoId=${item.equipo}`;
          }}
        >
          {value}
        </LinkEquipo>
      )
    },
    {
      key: 'equipo_descripcion',
      label: 'Descripción',
      sortable: true,
      sortKey: 'equipo__descripcion',
      width: '12%',
      render: (value) => value || '-'
    },
    {
      key: 'equipo_marca_nombre',
      label: 'Marca',
      sortable: true,
      sortKey: 'equipo__marca__nombre',
      width: '8%',
      render: (value) => value || '-'
    },
    {
      key: 'equipo_modelo_nombre',
      label: 'Modelo',
      sortable: true,
      sortKey: 'equipo__modelo__nombre',
      width: '8%',
      render: (value) => value || '-'
    },
    {
      key: 'equipo_familia_nombre',
      label: 'Familia',
      sortable: true,
      sortKey: 'equipo__familia__nombre',
      width: '8%'
    },
    {
      key: 'tipo_nombre',
      label: 'Tipo Documento',
      sortable: true,
      sortKey: 'tipo_nombre',
      width: '12%'
    },
    {
      key: 'fecha_emision',
      label: 'Emisión',
      sortable: true,
      sortKey: 'fecha_emision',
      width: '8%',
      render: (value) => {
        if (!value) return '-';
        const { formatDateDisplay } = require('../utils/dateUtils');
        return formatDateDisplay(value);
      }
    },
    {
      key: 'fecha_vencimiento',
      label: 'Vencimiento',
      sortable: true,
      sortKey: 'fecha_vencimiento',
      width: '9%',
      render: (value) => {
        if (!value) return '-';
        const { formatDateDisplay, parseDateLocal, toLocalYMD } = require('../utils/dateUtils');
        const isExpired = parseDateLocal(value) < parseDateLocal(toLocalYMD(new Date()));
        return (
          <span style={{ color: isExpired ? theme.colors.danger : theme.colors.inherit }}>
            {formatDateDisplay(value)}
          </span>
        );
      }
    },
    {
      key: 'archivo',
      label: 'Archivo',
      sortable: false,
      width: '9%',
      render: (value, item) => {
        const adjuntos = item.adjuntos || [];
        if (adjuntos.length === 0) return '-';
        return (
          <LinkAdjuntos
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDocumentoAdjuntos(item);
            }}
          >
            <i className="fas fa-file-pdf"></i> Ver{adjuntos.length > 1 ? ` (${adjuntos.length})` : ''}
          </LinkAdjuntos>
        );
      }
    },
    {
      key: 'notas',
      label: 'Notas',
      sortable: false,
      width: '16%',
      render: (value) => {
        if (!value) return '-';
        return value.length > 60 ? `${value.substring(0, 60)}...` : value;
      }
    }
  ];

  // ============================= CONFIGURACIÓN DE FILTROS =============================
  const filterConfig = [
    {
      key: 'equipo__interno__icontains',
      label: 'Equipo',
      type: 'text',
      placeholder: 'Filtrar por equipo...'
    },
    {
      key: 'equipo__descripcion__icontains',
      label: 'Descripción',
      type: 'text',
      sourceKey: 'equipo_descripcion',
      placeholder: 'Filtrar por descripción...'
    },
    {
      key: 'equipo__marca__nombre__iexact',
      label: 'Marca',
      type: 'text',
      sourceKey: 'equipo_marca_nombre',
      placeholder: 'Filtrar por marca...'
    },
    {
      key: 'equipo__modelo__nombre__iexact',
      label: 'Modelo',
      type: 'text',
      sourceKey: 'equipo_modelo_nombre',
      placeholder: 'Filtrar por modelo...'
    },
    {
      key: 'equipo__familia__nombre__iexact',
      label: 'Familia',
      type: 'text',
      sourceKey: 'equipo_familia_nombre',
      placeholder: 'Filtrar por familia...'
    },
    {
      key: 'tipo_nombre__icontains',
      label: 'Tipo',
      type: 'text',
      placeholder: 'Filtrar por tipo...'
    },
    {
      key: 'fecha_vencimiento__gte',
      label: 'Vencimiento desde',
      type: 'date',
      placeholder: ''
    },
    {
      key: 'fecha_vencimiento__lte',
      label: 'Vencimiento hasta',
      type: 'date',
      placeholder: ''
    },
    {
      key: 'numero_documento__icontains',
      label: 'Número',
      type: 'text',
      placeholder: 'Filtrar por número...'
    }
  ];

  // ============================= MANEJADORES DE EVENTOS =============================

  const handleAdd = () => {
    console.log('➕ Agregar documento');
    setEditingDocumento(null);
    setShowModal(true);
  };

  const handleEdit = (documento) => {
    console.log('✏️ Editar documento:', documento);
    setEditingDocumento(documento);
    setShowModal(true);
  };

  const handleDelete = async (documento) => {
    console.log('🗑️ Eliminar documento:', documento);
    // Aquí iría la lógica de eliminación
  };

  const handleFormSave = async () => {
    try {
      // Usar la referencia para refrescar la lista directamente
      if (listaUnificadaRef.current) {
        listaUnificadaRef.current.refreshImmediate();
      }

      return true;
    } catch (error) {
      console.error('❌ Error guardando documento:', error);
    }
  };

  const handleFormCancel = () => {
    console.log('❌ Cancelar formulario');
    setShowModal(false);
    setEditingDocumento(null);
  };

  // ============================= ACCIONES ADICIONALES =============================
  const additionalActions = [
    ...(puedeCargarMasivo ? [{
      label: 'Carga masiva',
      icon: 'fas fa-layer-group',
      variant: 'secondary',
      onClick: () => setShowCargaMasiva(true),
    }] : []),
  ];

  // ============================= RENDER =============================

  return (
    <ModuloShell>
      <ModuloHeader
        icono="bi bi-folder2-open"
        titulo="Documentación"
        descripcion="Documentos de los equipos, con sus vencimientos y archivos adjuntos."
        help={HELP}
        helpOpen={helpOpen}
        onToggleHelp={() => setHelpOpen(v => !v)}
      />

      <ListaUnificada
        ref={listaUnificadaRef}
        cloudScope="lista-documentacion.state.v1"
        titulo="Documentos"
        barraSeccion
        textoBotonAgregar="Nuevo documento"
        style={{ flex: 1, minHeight: 0 }}
        endpoint="documentacion/"
        useAdvancedFilters={true}
        tableVisualVariant="rows"
        columns={columns}
        filterConfig={filterConfig}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        searchPlaceholder="Buscar documentación..."
        itemsPerPageOptions={[10, 25, 50, 100]}
        defaultItemsPerPage={25}
        allowAdd={true}
        allowEdit={true}
        allowDelete={true}
        allowView={false}
        actionsColumnWidth="90px"
        additionalActions={additionalActions}
      />

      {/* Modal para formularios */}
      {showModal && (
        <ModalDocumentacion
          isOpen={showModal}
          onClose={handleFormCancel}
          documento={editingDocumento}
          onSaved={handleFormSave}
        />
      )}

      {/* Modal para ver/agregar/eliminar adjuntos */}
      {documentoAdjuntos && (
        <ModalAdjuntosDocumentacion
          isOpen={Boolean(documentoAdjuntos)}
          onClose={() => setDocumentoAdjuntos(null)}
          documento={documentoAdjuntos}
          onChanged={() => listaUnificadaRef.current?.refreshImmediate()}
        />
      )}

      {/* Modal de carga masiva de documentación */}
      {showCargaMasiva && (
        <ModalCargaMasivaDocumentacion
          onClose={() => setShowCargaMasiva(false)}
          onSaved={() => listaUnificadaRef.current?.refreshImmediate()}
        />
      )}
    </ModuloShell>
  );
};

export default ListaDocumentacion;